/**
 * Your app (section 7): the brand's menu screens; for the chosen screen its rows as an ordered
 * list (move, hide, remove, add from row types in plain words), each row's settings in a side
 * panel; and beside it a live preview of the screen as a TV or a phone, drawn from the draft
 * catalogue and the rows as they stand (saved or not), refreshed after every save.
 *
 * Hidden rows stay in the list but not in the app: the screen keeps them (hiddenRails, each with
 * its place) for the whole team, the feed build leaves them out, and showing one again puts it
 * back where it was.
 */

import { useEffect, useMemo, useState } from "react";
import Button from "@web-ui/components/Button";
import Checkbox from "@web-ui/components/Checkbox";
import Field from "@web-ui/components/Field";
import Modal from "@web-ui/components/Modal";
import Notice from "@web-ui/components/Notice";
import Segmented from "@web-ui/components/Segmented";
import { SkeletonCard } from "@web-ui/components/Skeleton";
import Switch from "@web-ui/components/Switch";
import { entities, type Entity } from "@/api/admin";
import { ApiError } from "@/api/client";
import { PageBar } from "@/components/page-bar";
import { SaveBar } from "@/components/save-bar";
import { StatusBadge } from "@/components/status-badge";
import { prepareForSave } from "@/forms/checks";
import { useCatalogue } from "@/forms/form-context";
import { PickerCard } from "@/forms/controls/picker";
import { go, href, type Route } from "@/lib/route";
import { usePreviewData, useBrandFonts } from "@/preview/data";
import { layoutScreen } from "@/preview/layout";
import { AppPreview, type Device } from "@/preview/preview";
import { hiddenOf, moveItem, newRail, railsOf, rowsFrom, rowSummary, rowTypeOf, rowTypes, type Rail, type RailKind, type RowEntry } from "@/screens/rows";
import { useStudio } from "@/shell/studio";
import { NewItem } from "./library";

type Hidden = Array<{ at: number; rail: Rail }>;
const hiddenOfPage = (page: Entity | undefined): Hidden => (page?.data["hiddenRails"] as Hidden | undefined) ?? [];

const text = (v: Record<string, string> | undefined, locale: string) => v?.[locale] || Object.values(v ?? {})[0] || "";

export function ScreensEditor({ route }: { route: Route }) {
  const { client, pack, canEdit, brandView, refresh, items, stateOf } = useStudio();
  const catalogue = useCatalogue();
  const brand = client.brand.id;
  const locale = client.brand.locales.default;
  const pages = useMemo(() => (catalogue ?? []).filter((e) => e.type === "page" && e.status !== "archived"), [catalogue]);
  const menu = useMemo(() => (brandView?.manifest.menu ?? []).map((m) => ({ id: m.pageId, label: text(m.label, locale) || m.pageId })), [brandView, locale]);
  const inMenu = menu.map((m) => pages.find((p) => p.id === m.id)).filter(Boolean) as Entity[];
  const others = pages.filter((p) => !menu.some((m) => m.id === p.id));
  const currentId = route.id ?? menu[0]?.id ?? pages[0]?.id;
  const page = pages.find((p) => p.id === currentId);

  const [rows, setRows] = useState<RowEntry[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [settings, setSettings] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [device, setDevice] = useState<Device>("tv");
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [message, setMessage] = useState<{ tone: "positive" | "critical"; text: string } | null>(null);

  // The editor's rows: the saved ones and the hidden ones the screen keeps.
  const savedRails = (page?.data["rails"] as Rail[] | undefined) ?? [];
  const savedHidden = hiddenOfPage(page);
  useEffect(() => {
    if (!page) return;
    setRows(rowsFrom((page.data["rails"] as Rail[] | undefined) ?? [], hiddenOfPage(page)));
    setSelected(null); setTried(false);
  }, [page?.id, page?.revision, brand]); // eslint-disable-line react-hooks/exhaustive-deps

  // From Review & publish: a problem in a row (rails.<n>.…) selects it and opens its settings.
  useEffect(() => {
    const m = route.field?.match(/^rails\.(\d+)/);
    if (!m || !rows.length) return;
    const visible = rows.map((r, i) => ({ r, i })).filter((x) => !x.r.hidden);
    const at = visible[Number(m[1])]?.i;
    if (at !== undefined) { setSelected(at); setSettings(at); }
  }, [route.field, rows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !!page && (JSON.stringify(railsOf(rows)) !== JSON.stringify(savedRails) || JSON.stringify(hiddenOf(rows)) !== JSON.stringify(savedHidden));
  const collections = (catalogue ?? []).filter((e) => e.type === "collection" && e.status !== "archived");
  const terms = (catalogue ?? []).filter((e) => e.type === "taxonomy").flatMap((t) => ((t.data["terms"] as Array<{ id: string; title: Record<string, string> }>) ?? []).map((x) => ({ id: x.id, name: text(x.title, locale), list: t.label })));
  const names = { collection: (id: string) => collections.find((c) => c.id === id)?.label, term: (id: string) => terms.find((t) => t.id === id)?.name };
  const has = {
    live: !!brandView?.manifest.features.live || (catalogue ?? []).some((e) => e.type === "liveEvent"),
    series: (catalogue ?? []).some((e) => e.type === "series"),
    programmes: (catalogue ?? []).some((e) => e.type === "series" && e.data["kind"] === "programme"),
    stages: pack.showsStages || (catalogue ?? []).some((e) => e.type === "stage"),
    categories: terms.length > 0,
  };
  const problems = rows.map((r) => (r.hidden ? undefined : rowSummary(r.rail, pack, names, locale).missing));
  const blocking = rows.filter((r, i) => !r.hidden && problems[i] && rowTypeOf(r.rail.kind, pack).needs && !(r.rail.collectionId || r.rail.collectionIds?.length || r.rail.termId));

  const save = async () => {
    if (!page) return;
    setTried(true); setMessage(null);
    if (blocking.length) return;
    setBusy(true);
    try {
      const hidden = hiddenOf(rows);
      const { hiddenRails: _old, ...rest } = page.data;
      await entities.save(client, "page", page.id, prepareForSave("page", { ...rest, rails: railsOf(rows), ...(hidden.length ? { hiddenRails: hidden } : {}) }), page.status, page.revision);
      await refresh();
      setMessage({ tone: "positive", text: page.status === "ready" ? "Saved. The screen changes in the apps at your next publish." : "Saved. This screen is a draft, so it is not in the apps yet." });
    } catch (e) {
      setMessage({ tone: "critical", text: e instanceof ApiError && e.code === "revision_conflict" ? "Someone else changed this screen meanwhile. Discard your changes to load theirs." : e instanceof ApiError && e.code === "invalid_entity" ? `Not saved: ${((e.body["issues"] as string[]) ?? []).join("; ")}` : "The save did not go through. Check your connection and try again." });
    } finally { setBusy(false); }
  };

  // The preview: the draft catalogue, with this screen's rows as they stand in the editor.
  const data = usePreviewData(client, catalogue, items);
  const fonts = useBrandFonts(client.brand);
  const screen = useMemo(() => data ? layoutScreen({
    feed: data.feed,
    rails: rows.map((r, index) => ({ rail: r.rail, index, hidden: r.hidden })).filter((r) => !r.hidden),
    rowLabel: (kind) => rowTypeOf(kind, pack).label, locale, now: Date.now(),
  }) : null, [data, rows, pack, locale]);

  if (catalogue === null) return <SkeletonCard label="Loading your screens" rows={6} />;
  const pick = (r: RowEntry, i: number, change: Partial<RowEntry>) => setRows(rows.map((x, j) => (j === i ? { ...x, ...change } : x)));
  const screenName = page ? (menu.find((m) => m.id === page.id)?.label ?? page.label) : "";

  return (
    <>
      <PageBar>
        {canEdit ? <Button type="button" variant="secondary" icon="plus" onClick={() => setCreating(true)}>New screen</Button> : null}
      </PageBar>
      {message ? <Notice tone={message.tone} onDismiss={() => setMessage(null)}>{message.text}</Notice> : null}
      {tried && blocking.length ? <Notice tone="critical" title="Not saved yet">Some rows still need something chosen. They are marked below.</Notice> : null}

      <div className="grid items-start gap-3 2xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="flex min-w-0 flex-col gap-3">
          <section className="card flex flex-col gap-3" aria-labelledby="screens-title">
            <div className="ps-card-inset">
              <h2 id="screens-title" className="text-sub-title-1">Screens</h2>
              <p className="text-body-2 text-t-secondary">The tabs of your app's menu, in order. Change the menu under <a className="underline" href={href({ brand, section: "settings", page: "brand" })}>Settings → Brand</a>.</p>
            </div>
            <ul className="flex flex-col gap-1">
              {inMenu.map((p) => <ScreenLink key={p.id} entity={p} label={menu.find((m) => m.id === p.id)!.label} current={p.id === currentId} state={stateOf("page", p.id)} />)}
            </ul>
            {others.length ? (
              <>
                <h3 className="text-sub-title-2 ps-card-inset">Not in the menu</h3>
                <ul className="flex flex-col gap-1">{others.map((p) => <ScreenLink key={p.id} entity={p} label={p.label} current={p.id === currentId} state={stateOf("page", p.id)} />)}</ul>
              </>
            ) : null}
            {!pages.length ? <p className="text-body-2 text-t-secondary ps-card-inset">No screens yet. Add your first one: it becomes a tab once it is in the menu.</p> : null}
          </section>

          {page ? (
            <section className="card flex flex-col gap-3" aria-labelledby="rows-title">
              <div className="ps-card-inset">
                <h2 id="rows-title" className="text-sub-title-1">{`Rows on ${screenName}`}</h2>
                <p className="text-body-2 text-t-secondary">Top to bottom, as viewers see them. Select a row to see it in the preview.</p>
              </div>
              {rows.length ? (
                <ol className="flex flex-col gap-2">
                  {rows.map((r, i) => {
                    const sum = rowSummary(r.rail, pack, names, locale);
                    const needs = tried && blocking.includes(r);
                    return (
                      <li key={r.key} className={`flex flex-col gap-2 rounded-2xl p-3 transition-colors ${selected === i ? "bg-b-surface1 ring-2 ring-accent" : "bg-b-surface1"} ${r.hidden ? "opacity-60" : ""}`}>
                        <div className="flex items-start gap-3">
                          <span className="mt-0.5 w-6 shrink-0 text-center text-caption text-t-secondary tabular-nums">{i + 1}</span>
                          <button type="button" className="flex min-w-0 grow flex-col text-start focus-visible:outline-2 focus-visible:outline-s-focus" aria-pressed={selected === i} onClick={() => setSelected(i)}>
                            <span className="truncate text-body-2 font-medium">{sum.title}</span>
                            <span className="truncate text-caption text-t-secondary">{r.hidden ? `Hidden · ${sum.type}` : sum.type}</span>
                          </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 ps-9">
                          {canEdit ? (
                            <label className="flex items-center gap-2 text-caption text-t-secondary">
                              <Switch checked={!r.hidden} onChange={(on) => pick(r, i, { hidden: !on })} ariaLabel={`Show “${sum.title}” in the app`} />
                              {r.hidden ? "Hidden" : "Shown"}
                            </label>
                          ) : null}
                          {sum.missing && !r.hidden ? <span className={`text-caption ${needs ? "text-critical" : "text-t-secondary"}`}>{sum.missing}{rowTypeOf(r.rail.kind, pack).needs ? " " : ""}{rowTypeOf(r.rail.kind, pack).needs && canEdit ? <button type="button" className="underline" onClick={() => { setSelected(i); setSettings(i); }}>Choose</button> : null}</span> : null}
                          {canEdit ? (
                            <div className="ms-auto flex shrink-0 items-center gap-1">
                              <Button type="button" size="xs" variant="ghost" icon="arrow-up" isIconOnly aria-label={`Move “${sum.title}” up`} disabled={i === 0} onClick={() => { setRows(moveItem(rows, i, i - 1)); setSelected(i - 1); }} />
                              <Button type="button" size="xs" variant="ghost" icon="arrow-down" isIconOnly aria-label={`Move “${sum.title}” down`} disabled={i === rows.length - 1} onClick={() => { setRows(moveItem(rows, i, i + 1)); setSelected(i + 1); }} />
                              <Button type="button" size="xs" variant="ghost" icon="edit" isIconOnly aria-label={`Settings of “${sum.title}”`} onClick={() => { setSelected(i); setSettings(i); }} />
                              <Button type="button" size="xs" variant="ghost" icon="trash" isIconOnly aria-label={`Remove “${sum.title}”`} onClick={() => { setRows(rows.filter((_, j) => j !== i)); setSelected(null); }} />
                            </div>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : <p className="text-body-2 text-t-secondary ps-card-inset">No rows yet.</p>}
              {canEdit ? <div><Button type="button" size="sm" variant="secondary" icon="plus" onClick={() => setAdding(true)}>Add a row</Button></div> : null}
            </section>
          ) : null}
        </div>

        <section className="card flex min-w-0 flex-col gap-3 2xl:sticky 2xl:top-24" aria-labelledby="preview-title">
          <div className="flex flex-wrap items-center gap-3 ps-card-inset">
            <h2 id="preview-title" className="me-auto text-sub-title-1">Preview</h2>
            <Segmented size="sm" label="Show the preview as" options={[{ id: "tv", label: "TV" }, { id: "phone", label: "Phone" }]} value={device} onChange={(v) => setDevice(v)} />
          </div>
          {screen && page ? (
            <AppPreview screen={screen} device={device} brandName={client.brand.name} menu={menu.length ? menu : [{ id: page.id, label: screenName }]} currentId={page.id} fonts={fonts} selected={selected} onSelect={setSelected} />
          ) : page ? <SkeletonCard label="Drawing the preview" rows={4} /> : null}
          <p className="text-caption text-t-secondary">
            A sketch of your app's layout with your saved and unsaved changes and real artwork; the apps themselves differ in detail.
            {data && data.leftOut.length ? ` ${data.leftOut.length} ${data.leftOut.length === 1 ? "item is" : "items are"} not in the preview yet: no video.` : ""}
          </p>
        </section>
      </div>

      {canEdit ? <SaveBar dirty={dirty} saving={busy} onSave={() => void save()} onDiscard={() => page && setRows(rowsFrom(savedRails, savedHidden))} note="The screen changes in the apps at your next publish." /> : null}

      <AddRow open={adding} onClose={() => setAdding(false)} types={rowTypes(pack, has)} onAdd={(kind) => {
        const next = [...rows, { key: `n${Date.now()}`, rail: newRail(kind), hidden: false }];
        setRows(next); setSelected(next.length - 1); setAdding(false);
        if (rowTypeOf(kind, pack).needs) setSettings(next.length - 1);
      }} />
      {settings !== null && rows[settings] ? (
        <RowSettings key={rows[settings]!.key} rail={rows[settings]!.rail} collections={collections} terms={terms} locale={locale} canEdit={canEdit}
          onChange={(rail) => setRows(rows.map((x, j) => (j === settings ? { ...x, rail } : x)))} onClose={() => setSettings(null)} />
      ) : null}
      <NewItem open={creating} type="page" taken={new Set(pages.map((p) => p.id))} onClose={() => setCreating(false)} onCreated={async (id) => { await refresh(); setCreating(false); go({ brand, section: "app", type: "page", id }); }} />
    </>
  );
}

function ScreenLink({ entity, label, current, state }: { entity: Entity; label: string; current: boolean; state: string | undefined }) {
  const { client } = useStudio();
  return (
    <li>
      <a href={href({ brand: client.brand.id, section: "app", type: "page", id: entity.id })} aria-current={current ? "page" : undefined}
        className={`flex items-center gap-3 rounded-2xl px-3 py-2 transition-colors focus-visible:outline-2 focus-visible:outline-s-focus ${current ? "bg-b-surface1 ring-2 ring-accent" : "hover:bg-b-surface1"}`}>
        <span className="min-w-0 grow truncate text-body-2 font-medium">{label}</span>
        <span className="text-caption text-t-secondary">{(() => { const n = ((entity.data["rails"] as unknown[]) ?? []).length; return `${n} ${n === 1 ? "row" : "rows"}`; })()}</span>
        <StatusBadge state={state ?? entity.publishState} />
      </a>
    </li>
  );
}

function AddRow({ open, onClose, types, onAdd }: { open: boolean; onClose: () => void; types: ReturnType<typeof rowTypes>; onAdd: (kind: RailKind) => void }) {
  return (
    <Modal open={open} onClose={onClose} size="md">
      <div className="flex flex-col gap-4">
        <h2 className="text-h5">Add a row</h2>
        <ul className="flex flex-col gap-2">
          {types.map((t) => (
            <li key={t.kind}>
              <button type="button" onClick={() => onAdd(t.kind)} className="flex w-full flex-col rounded-2xl bg-b-surface1 p-3 text-start transition-colors hover:bg-b-surface3 focus-visible:outline-2 focus-visible:outline-s-focus">
                <span className="text-body-2 font-medium">{t.label}{t.personal ? <span className="ms-2 text-caption text-t-secondary">· different for every viewer</span> : null}</span>
                <span className="text-caption text-t-secondary">{t.description}</span>
              </button>
            </li>
          ))}
        </ul>
        <div><Button type="button" variant="outline" onClick={onClose}>Cancel</Button></div>
      </div>
    </Modal>
  );
}

/** One row's settings, in a side panel: its title, and what it shows (a collection, collections, a category, how many). */
function RowSettings({ rail, collections, terms, locale, canEdit, onChange, onClose }: { rail: Rail; collections: Entity[]; terms: Array<{ id: string; name: string; list: string }>; locale: string; canEdit: boolean; onChange: (rail: Rail) => void; onClose: () => void }) {
  const { pack, client } = useStudio();
  const type = rowTypeOf(rail.kind, pack);
  const own = rail.title?.[locale] ?? "";
  const set = (patch: Partial<Rail>) => {
    const next = { ...rail, ...patch } as Rail;
    for (const k of Object.keys(next) as Array<keyof Rail>) if (next[k] === undefined || next[k] === "") delete next[k];
    onChange(next);
  };
  const lists = [...new Set(terms.map((t) => t.list))];
  return (
    <Modal open onClose={onClose} isSlidePanel>
      <div className="flex h-full flex-col gap-5 overflow-y-auto p-6 pt-20 max-md:p-4 max-md:pt-18">
        <div>
          <h2 className="text-h5">{type.label}</h2>
          <p className="text-body-2 text-t-secondary">{type.description}</p>
        </div>
        <Field label="Row title" optional placeholder={rail.kind === "collection" && rail.collectionId ? collections.find((c) => c.id === rail.collectionId)?.label : type.label} disabled={!canEdit}
          description="Leave empty to use the name shown here in grey." value={own}
          onChange={(e) => set({ title: e.target.value ? { ...(rail.title ?? {}), [locale]: e.target.value } : (() => { const t = { ...(rail.title ?? {}) }; delete t[locale]; return Object.keys(t).length ? t : undefined; })() })} />
        {type.needs === "collection" ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-sub-title-2">Which collection</legend>
            {collections.length ? collections.map((c) => (
              <label key={c.id} className={`block cursor-pointer rounded-2xl ${rail.collectionId === c.id ? "ring-2 ring-accent" : ""}`}>
                <input type="radio" name="row-collection" className="sr-only" checked={rail.collectionId === c.id} disabled={!canEdit} onChange={() => set({ collectionId: c.id })} />
                <PickerCard entity={c} link={false} />
              </label>
            )) : <p className="text-body-2 text-t-secondary">No collections yet.</p>}
            <a className="text-body-2 underline" href={href({ brand: client.brand.id, section: "library", type: "collection", show: "new" })}>Make a new collection</a>
          </fieldset>
        ) : null}
        {type.needs === "collections" ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-sub-title-2">Which collections, in order</legend>
            {collections.map((c) => (
              <Checkbox key={c.id} label={c.label} disabled={!canEdit} checked={(rail.collectionIds ?? []).includes(c.id)}
                onChange={(on) => set({ collectionIds: on ? [...(rail.collectionIds ?? []), c.id] : (rail.collectionIds ?? []).filter((x) => x !== c.id) })} />
            ))}
          </fieldset>
        ) : null}
        {type.needs === "term" ? (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-2 text-sub-title-2">{`Which ${pack.nouns.taxonomy.one.toLowerCase()}`}</legend>
            {lists.map((l) => (
              <div key={l} className="flex flex-col gap-2">
                <span className="text-caption text-t-secondary">{l}</span>
                {terms.filter((t) => t.list === l).map((t) => (
                  <label key={t.id} className="flex cursor-pointer items-center gap-3 text-body-2">
                    <input type="radio" name="row-term" className="size-4 accent-accent" checked={rail.termId === t.id} disabled={!canEdit} onChange={() => set({ termId: t.id })} />
                    {t.name}
                  </label>
                ))}
              </div>
            ))}
          </fieldset>
        ) : null}
        {type.limit ? (
          <Field label="Show at most" type="number" min={1} optional disabled={!canEdit} description="Leave empty for the apps' usual number." value={rail.limit === undefined ? "" : String(rail.limit)}
            onChange={(e) => set({ limit: e.target.value === "" ? undefined : Math.max(1, Math.round(Number(e.target.value))) })} />
        ) : null}
        <div><Button type="button" variant="primary" onClick={onClose}>Done</Button></div>
      </div>
    </Modal>
  );
}
